import { AuthenticationClient } from "authing-js-sdk";
import {
    getValue4Lang,
    isEmpty,
    clearCache,
    setAuthenticatedLocationHash,
    getUrlHashValue,
    combineUrl,
    getErrorParams,
    getAuthCurrentLang,
    parseQueryString,
    parseUrlHash,
    getQueryStringValue,
    setCacheAuthState,
    getCacheAuthState,
} from '~/common';
import { getContextByKey, exceptionLog } from '~/client';
import { CTMModal } from '~/components';
import { store } from '~/index.js';
import enUsMessages from '~/locales/en-us/messages';
import zhCnMessages from '~/locales/zh-cn/messages';
import zhTwMessages from '~/locales/zh-tw/messages';
import jaJpMessages from '~/locales/ja-jp/messages';

const formatMessage = ({ id, values }) => getValue4Lang(enUsMessages, zhCnMessages, zhTwMessages, jaJpMessages)[id].replace('{0}', Object.values(values || {})[0]);

export const PREFIX = 'ctmsmartportal';
export const ACCESS_TOKEN = `${PREFIX}_access_token`;
export const REFRESH_TOKEN = `${PREFIX}_refresh_token`;
export const ID_TOKEN = `${PREFIX}_id_token`;
export const EXPIRES_AT = `${PREFIX}_expires_at`;
export const SCOPES = `${PREFIX}_scopes`;
export const LOGOUT_URL = `${PREFIX}_logout_url`;
export const CHECK_SESSION_AT = `${PREFIX}_check_session_at`;

export default class Authing {
    tokenRenewalLoading;
    tokenRenewalQueue;
    authingOptions;
    authing;
    config;
    authExpireTimeout;

    constructor(config) {
        this.tokenRenewalQueue = [];
        this.config = config;
        this.authingOptions = {
            appId: this.config.authClientID,
            // appSecret: "",
            appHost: this.config.authDomain,
            redirectUri: this.config.authRedirectUri,
            // scope: 'openid profile email',
            tokenEndPointAuthMethod: 'none',
            protocol: 'oidc',
            lang: getValue4Lang('en', 'zh', 'zh-TW', 'ja', getAuthCurrentLang())
        };
        this.authing = new AuthenticationClient(this.authingOptions);
        this.isAuthenticated() && this.scheduleAuthExpire();
    }

    login(options) {
        window.location.href = this.authing.buildAuthorizeUrl({ scope: 'openid profile email offline_access' });
    }
    async getRedirectUri() {
        this.queryObj = parseQueryString();
        let url = this.config.authRedirectUri;
        if (this.queryObj['ContextKey']) {
            await getContextByKey({ "ContextKey": this.queryObj['ContextKey'] }, (err, data) => {
                if (data && data.Context) {
                    //console.log(data)
                    url = data.Context;
                }
            });
        }
        return url;
    }
    extSSOLogin(options) {
        window.location.href = `${this.config.authRedirectUri}?esp=1&code=${options.code}&nonce=${this.config.authExternalLinkKey}&state=${this.config.authExternalLinkKey}`;
    }

    autoLogin(email, pwd, authConnection, redirectUri) {
        this.authing.loginByEmail(email, pwd).then(result => {
            if (redirectUri) {
                window.location.href = redirectUri;
            } else {
                const state = new Date().getTime().toString();
                setCacheAuthState({ state });
                window.location.href = this.config.authDomain + "/oidc/auth?client_id=" + this.config.authClientID + "&scope=openid+email+profile&state=" + state + "&nonce=nonce&response_type=code&redirect_uri=" + this.config.authRedirectUri;
            }
        }).catch(error => {
            this.setExceptionLog(error, email);
        });
    }
    setExceptionLog(error, email) {
        const params = {
            ...getErrorParams(new Error('Authing_LoginFail'), JSON.stringify({ errObj: error, opts: this.authingOptions }, null, 4)),
            userEmail: email,
            userName: email,
        };
        exceptionLog(params);
    }

    handleAuthentication(callback) {
        var hashOptions = { __enableIdPInitiatedLogin: true };
        var extContextId = getUrlHashValue(window.location.href, "ext_context_id") || '';
        var useExtSsoPage = getQueryStringValue(window.location.search, "esp") == 1;
        var route = getUrlHashValue(window.location.href, "route") || getQueryStringValue(window.location.href, "route") || '';
        var authContext = getUrlHashValue(window.location.href, "authContext") || getQueryStringValue(window.location.href, "authContext") || '';

        if (!isEmpty(this.config.authExternalLinkKey)) {
            if (!isEmpty(extContextId) || useExtSsoPage) {
                hashOptions = { "state": this.config.authExternalLinkKey, "nonce": "" };
            }
            else {
                if (!isEmpty(document.referrer) && document.referrer.toLowerCase().indexOf(this.config.localhostUrl.toLowerCase()) < 0) {
                    var dcLinkKey = window.atob(this.config.authExternalLinkKey).toLowerCase();
                    if (document.referrer.toLowerCase().indexOf(dcLinkKey) > 0) {
                        hashOptions = { "state": this.config.authExternalLinkKey, "nonce": this.config.authExternalLinkKey };
                    }
                }
            }
        }


        this.parseHash(hashOptions, (err, authResult) => {
            if (authResult && authResult.accessToken && authResult.idToken) {
                setAuthenticatedLocationHash(window.location.hash);
                const userInfo = this.setSession(authResult, {});
                const stateObj = getCacheAuthState();
                const authUser = { ...userInfo, extContextId, route, authContext, isAutoLogin: Boolean(stateObj.state && authResult.state == stateObj.state) };
                typeof callback === 'function' && callback(authUser);
            } else if (err) {
                const params = {
                    ...getErrorParams(new Error('Authing_ParseHashFail'), JSON.stringify({
                        errObj: err,
                        authRes: authResult,
                        opts: hashOptions,
                        href: window.location.href,
                        query: parseQueryString(),
                        hash: parseUrlHash(window.location.hash),
                    }, null, 4)),
                    userEmail: '',
                    userName: '',
                };
                if ('sendBeacon' in navigator) {
                    navigator.sendBeacon(combineUrl(this.config.obtApiUrl, 'Common/V1/json/ExceptionLog'), JSON.stringify(params))
                } else {
                    exceptionLog(params);
                }
                this.config.isDebug && console.log(err);
                this.clearData(false);
                if (err.error === 'access_denied') {
                    this.logout();
                    return;
                }
                if (this.config.isDebug) {
                    alert(`${err}. Check the console for further details.(show in debug mode)`);
                }
                this.logout();
            }
        });
    }
    async parseHash(options, callback) {
        this.queryObj = parseQueryString();
        let tokenInfo = {};
        let err = null;
        let url = await this.getRedirectUri();
        if (this.queryObj['code']) {
            tokenInfo = await new AuthenticationClient({
                appId: this.config.authClientID,
                appHost: this.config.authDomain,
                redirectUri: url,
                tokenEndPointAuthMethod: 'none',
                protocol: 'oidc',
                lang: getValue4Lang('en', 'zh', 'zh-TW', 'ja', getAuthCurrentLang())
            }).getAccessTokenByCode(this.queryObj['code']).catch(error => {
                err = error;
                return {};
            });
            tokenInfo.state = this.queryObj['state'];
        } else {
            const hash = window.location.hash.substring(1);
            // 解析哈希值中的参数。哈希值通常形如 'key1=value1&key2=value2'，我们可以像解析查询字符串一样处理它
            const params = new URLSearchParams(hash);
            // 将参数转换为对象
            const parsedParams = {};
            for (const [key, value] of params.entries()) {
                parsedParams[key] = value;
            }
            // console.log(parsedParams);
            tokenInfo = parsedParams;
        }
        let email = await this.getUserEmail(tokenInfo.access_token);
        // console.log(tokenInfo, email)

        callback(err, this.getAuthResult(tokenInfo, email));
    }

    getAuthResult(tokenInfo, email) {
        return {
            expiresIn: tokenInfo.expires_in,
            idTokenPayload: { sub: '', email: email },
            scope: tokenInfo.scope,
            accessToken: tokenInfo.access_token,
            refreshToken: tokenInfo.refresh_token,
            idToken: tokenInfo.id_token,
            state: tokenInfo.state,
        };
    }

    async getUserEmail(accessToken) {
        // this.authing.setAccessToken(accessToken);
        const userInfo = await this.authing.getUserInfoByAccessToken(accessToken).catch(error => { return null });
        return userInfo ? userInfo.email : '';
    }

    setSession(authResult, { isRenewal = false, renewServerDelay }) {
        // Set the time that the access token will expire at
        let expiresAt = JSON.stringify((authResult.expiresIn * 1000) + new Date().getTime());
        let logoutUrl = "";
        // If there is a value on the `scope` param from the authResult,
        // use it to set scopes in the session for the user. Otherwise
        // use the scopes as requested. If no scopes were requested,
        // set it to nothing
        if (this.config.externalSsoIdentifier) {
            let identifier = this.config.externalSsoIdentifier.split(',');
            logoutUrl = (identifier.some(i => authResult.idTokenPayload.sub.includes(i)) && combineUrl(this.config.localhostUrl, 'sign-out')) || "";
        }
        const scopes = authResult.scope || '';
        const userInfo = this.formatUser(authResult.idTokenPayload);
        localStorage.setItem(ACCESS_TOKEN, authResult.accessToken);
        localStorage.setItem(REFRESH_TOKEN, authResult.refreshToken);
        localStorage.setItem(ID_TOKEN, authResult.idToken);
        localStorage.setItem(EXPIRES_AT, expiresAt);
        localStorage.setItem(SCOPES, JSON.stringify(scopes));
        localStorage.setItem(LOGOUT_URL, logoutUrl);
        // if (!isRenewal) {
        //   localStorage.setItem(LOGOUT_URL, logoutUrl);
        // } else {
        //   localStorage.setItem(LOGOUT_URL, "");
        // }
        //localStorage.setItem(DEBTOR_ID, authResult.idTokenPayload['https://www.ctmsmart.com/debtor']);
        // schedule a token renewal
        // console.log(renewServerDelay, authResult.expiresIn * 1000)
        this.scheduleRenewal(renewServerDelay || authResult.expiresIn * 1000);
        this.scheduleAuthExpire();
        return userInfo;
    }

    logout() {
        var logoutUrl = localStorage.getItem(LOGOUT_URL);
        var idToken = localStorage.getItem(ID_TOKEN);
        // Clear access token and ID token from local storage

        if (isEmpty(logoutUrl)) {
            logoutUrl = combineUrl(this.config.localhostUrl, 'login');
        }
        let options = null;
        let url = logoutUrl;
        if (logoutUrl && idToken) {
            options = {
                expert: true,
                redirectUri: logoutUrl,
                // 用户的 id_token 可以通过登录接口获取
                idToken: idToken,
                // state: this.config.authExternalLinkKey,
                // protocol: 'oidc',
            }
            url = this.authing.buildLogoutUrl(options);
        }

        this.clearData(false);

        window.location.href = url;
    }

    clearData(clearLogoutUrl = true) {
        localStorage.removeItem(ACCESS_TOKEN);
        localStorage.removeItem(REFRESH_TOKEN);
        localStorage.removeItem(ID_TOKEN);
        localStorage.removeItem(EXPIRES_AT);
        localStorage.removeItem(CHECK_SESSION_AT);
        localStorage.removeItem(SCOPES);
        if (clearLogoutUrl) {
            localStorage.removeItem(LOGOUT_URL);
        }
        clearCache();
        this.tokenRenewalLoading = false;
        this.tokenRenewalQueue = [];
    }

    renewToken() {
        return new Promise(resolve => {
            if (this.tokenRenewalLoading) {
                this.tokenRenewalQueue.push(resolve);
                return;
            }
            this.tokenRenewalLoading = true;
            // console.log(localStorage.getItem(ACCESS_TOKEN), localStorage.getItem(REFRESH_TOKEN))
            this.authing.getNewAccessTokenByRefreshToken(localStorage.getItem(REFRESH_TOKEN)).then(async result => {
                // console.log(result)
                this.tokenRenewalLoading = false;
                if (result && result.access_token && result.id_token) {
                    localStorage.setItem(REFRESH_TOKEN, result.refresh_token)

                    const authEmail = await this.getUserEmail(result.access_token);
                    const user = store.getState().user;
                    // console.log(authEmail.toLowerCase(), user.email.toLowerCase())
                    if (authEmail.toLowerCase() != user.email.toLowerCase()) {
                        window.clearTimeout(this.authExpireTimeout);
                        CTMModal.error({
                            className: 'ctm-modal-no-close-icon',
                            centered: true,
                            content: formatMessage({ id: 'diff_user_tip_fmt', values: [authEmail] }),
                            onOk: () => this.login(),
                            okText: formatMessage({ id: 'refresh' }),
                        });
                        return;
                    }
                    this.setSession(this.getAuthResult(result, authEmail), { isRenewal: true, renewServerDelay: user.checkSessionInterval });
                    resolve();
                    while (this.tokenRenewalQueue.length) {
                        const func = this.tokenRenewalQueue.shift();
                        func();
                    };
                    console.log(`Successfully renewed auth!`);
                }
                else {
                    localStorage.removeItem(ACCESS_TOKEN);
                }
            }).catch(error => {
                console.error(
                    `Could not get a new token (${error}).`
                );
                localStorage.removeItem(ACCESS_TOKEN);
                window.clearTimeout(this.authExpireTimeout);
                CTMModal.error({
                    className: 'ctm-modal-no-close-icon',
                    centered: true,
                    content: formatMessage({ id: 'timeout_tip' }) + (this.config.isDebug ? ' (AU0050)' : ''),
                    onOk: () => {
                        this.clearData(false);
                        window.location.href = this.config.localhostUrl;
                    },
                });
            });
        });
    }

    scheduleRenewal(serverDelay) {
        let checkSessionAt = JSON.stringify(serverDelay + new Date().getTime());
        localStorage.setItem(CHECK_SESSION_AT, checkSessionAt);
    }

    scheduleAuthExpire() {
        window.clearTimeout(this.authExpireTimeout);
        const expiresAt = JSON.parse(localStorage.getItem(EXPIRES_AT));
        const delay = expiresAt - Date.now();
        if (delay > 0) {
            this.authExpireTimeout = setTimeout(() => {
                CTMModal.error({
                    className: 'ctm-modal-no-close-icon',
                    centered: true,
                    content: formatMessage({ id: 'timeout_tip' }) + (this.config.isDebug ? ' (AU0050)' : ''),
                    onOk: () => {
                        this.clearData(false);
                        window.location.href = this.config.localhostUrl;
                    },
                });
            }, delay);
        }
    }

    isAuthenticated() {
        // Check whether the current time is past the
        // access token's expiry time
        let expiresAt = JSON.parse(localStorage.getItem(EXPIRES_AT));
        return new Date().getTime() < expiresAt;
    }

    isNeedCheckSession() {
        let expiresAt = JSON.parse(localStorage.getItem(CHECK_SESSION_AT));
        return new Date().getTime() > expiresAt;
    }

    userHasScopes(scopes) {
        const grantedScopes = (JSON.parse(localStorage.getItem(SCOPES)) || '').split(' ');
        return scopes.every(scope => grantedScopes.includes(scope));
    }

    formatUser(orgUser) {
        const { nickname, name, email, picture, sub } = orgUser;

        return { nickname, name, email, picture, sub };
    }
}
